// Utilities to estimate the passing time at each locality of a route on the
// client side, and to match those passing times against the hourly forecast.
//
// Rationale: each locality carries a `timeToArrive` (seconds from the route
// start), but the intermediate values are computed with a scaling bug in
// MapFrame (~10x too small). The LAST locality's `timeToArrive` is the real
// Google Directions duration, so we anchor to it and distribute the elapsed
// time proportionally to the cumulative great-circle distance between
// localities. This is robust for both freshly calculated and stored routes and
// does not depend on the buggy intermediate values.

const EARTH_RADIUS_M = 6371000;

// Default speeds (km/h) used only as a fallback when the route has no reliable
// total duration to anchor to.
const FALLBACK_SPEED_KMH = 100;

const toRad = deg => (deg * Math.PI) / 180;

// Resolves lat/lng from a locality regardless of storage format:
// - new format: localityPoint: { lat, lng }
// - old format: point: [lat, lng]
function getLatLng(locality) {
  if (!locality) return null;
  if (locality.localityPoint && locality.localityPoint.lat !== undefined) {
    return { lat: locality.localityPoint.lat, lng: locality.localityPoint.lng };
  }
  if (Array.isArray(locality.point)) {
    return { lat: locality.point[0], lng: locality.point[1] };
  }
  return null;
}

// Great-circle distance between two {lat, lng} points, in meters.
export function haversineMeters(a, b) {
  if (!a || !b) return 0;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Returns a new array of localities, each enriched with a `passingTime` (Date),
 * estimating when the traveler passes through that locality.
 *
 * @param {Array} localities - route localities (in order)
 * @param {Date}  departureTime - departure moment
 */
export function computePassingTimes(localities, departureTime, overrideTotalSeconds) {
  if (!Array.isArray(localities) || localities.length === 0) return localities;

  const start =
    departureTime instanceof Date && !isNaN(departureTime)
      ? departureTime
      : new Date();

  // Cumulative great-circle distance up to each locality.
  const cumDist = [0];
  for (let i = 1; i < localities.length; i++) {
    const prev = getLatLng(localities[i - 1]);
    const curr = getLatLng(localities[i]);
    cumDist[i] = cumDist[i - 1] + haversineMeters(prev, curr);
  }
  const totalDist = cumDist[cumDist.length - 1];

  // Anchor: prefer an explicit override (passed from the pre-deduplication finish
  // point), then fall back to the last locality's timeToArrive, then estimate.
  const lastTimeToArrive = Number(localities[localities.length - 1]?.timeToArrive);
  let totalSeconds;
  if (Number.isFinite(overrideTotalSeconds) && overrideTotalSeconds > 0) {
    totalSeconds = overrideTotalSeconds;
  } else if (Number.isFinite(lastTimeToArrive) && lastTimeToArrive > 0) {
    totalSeconds = lastTimeToArrive;
  } else {
    // Fallback: estimate from distance and a default speed.
    totalSeconds = (totalDist / 1000 / FALLBACK_SPEED_KMH) * 3600;
  }

  return localities.map((locality, i) => {
    const fraction = totalDist > 0 ? cumDist[i] / totalDist : 0;
    const elapsedSeconds = totalSeconds * fraction;
    const passingTime = new Date(start.getTime() + elapsedSeconds * 1000);
    return { ...locality, elapsedSeconds, passingTime };
  });
}

/**
 * Finds the index in the merged forecast timeline (hours[0] + hours[1], up to
 * ~48h) whose hour-of-day matches the given passing time. Returns 0 when no
 * match is found (e.g. passing time outside the available forecast window).
 *
 * @param {Array} weatherForecast - locality.weatherForecast
 * @param {Date}  passingTime
 */
export function findForecastHourIndex(weatherForecast, passingTime) {
  if (!Array.isArray(weatherForecast) || weatherForecast.length === 0) return 0;
  if (!(passingTime instanceof Date) || isNaN(passingTime)) return 0;

  const merged = (weatherForecast[0]?.hours || []).concat(
    weatherForecast[1]?.hours || []
  );
  if (merged.length === 0) return 0;

  const targetHour = passingTime.getHours();

  // The merged timeline is ordered chronologically. Walk it tracking day
  // rollovers (period wraps from 23 back to a lower value) so we land on the
  // correct occurrence of the target hour.
  let bestIndex = -1;
  let prevPeriod = null;
  let dayOffset = 0;
  const targetDayOffset = computeTargetDayOffset(merged, passingTime);

  for (let i = 0; i < merged.length; i++) {
    const period = parseInt(merged[i].period, 10);
    if (Number.isNaN(period)) continue;
    if (prevPeriod !== null && period < prevPeriod) {
      dayOffset += 1;
    }
    prevPeriod = period;

    if (period === targetHour && dayOffset === targetDayOffset) {
      bestIndex = i;
      break;
    }
    if (bestIndex === -1 && period === targetHour) {
      // Remember the first hour match as a fallback if the exact day is missing.
      bestIndex = i;
    }
  }

  return bestIndex === -1 ? 0 : bestIndex;
}

/**
 * Returns the forecast hour object that matches the passing time at a locality,
 * used to pick the correct weather icon on the map. Falls back to the first
 * available hour.
 */
export function getForecastAtPassingTime(weatherForecast, passingTime) {
  if (!Array.isArray(weatherForecast) || weatherForecast.length === 0) {
    return null;
  }
  const merged = (weatherForecast[0]?.hours || []).concat(
    weatherForecast[1]?.hours || []
  );
  if (merged.length === 0) return null;
  const idx = findForecastHourIndex(weatherForecast, passingTime);
  return merged[idx] || merged[0] || null;
}

/** Formats a passing-time Date as a local "HH:mm" string ('' when invalid). */
export function formatPassingTime(date) {
  if (!(date instanceof Date) || isNaN(date)) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Estimates how many days ahead the passing time is relative to the first
// forecast hour (0 = same day as the start of the timeline).
function computeTargetDayOffset(merged, passingTime) {
  const firstPeriod = parseInt(merged[0]?.period, 10);
  if (Number.isNaN(firstPeriod)) return 0;
  // The first timeline entry corresponds to "now"-ish; if the passing time is
  // on a later calendar day, bump the offset.
  const now = new Date();
  const dayDiff = Math.floor(
    (startOfDay(passingTime) - startOfDay(now)) / 86400000
  );
  return Math.max(0, dayDiff);
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Returns a copy of `weatherForecast` with the first day's hours trimmed to
 * those at/after the departure hour, so the timeline does not show hours that
 * are already in the past relative to the chosen departure. Only applies when
 * the departure is on the current calendar day; otherwise the forecast is
 * returned untouched (the forecast only spans ~48h, so future-day planning
 * keeps whatever hours are available).
 */
export function filterForecastFromDeparture(weatherForecast, departureTime) {
  if (!Array.isArray(weatherForecast) || weatherForecast.length === 0) {
    return weatherForecast;
  }
  const dep =
    departureTime instanceof Date && !isNaN(departureTime)
      ? departureTime
      : new Date();

  const isToday = startOfDay(dep) === startOfDay(new Date());
  if (!isToday) return weatherForecast;

  const depHour = dep.getHours();
  return weatherForecast.map((day, idx) => {
    if (idx !== 0 || !day?.hours) return day;
    return {
      ...day,
      hours: day.hours.filter(entry => parseInt(entry.period, 10) >= depHour),
    };
  });
}
